import { useCallback, useEffect, useMemo, useState } from "react";
import { BadgeCheck, ShieldCheck, PenLine, Quote } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { Reveal, Overline } from "./Reveal";
import { Stars } from "./Stars";
import { ReviewDialog } from "./ReviewDialog";

const ReviewCard = ({ r, t, i }) => (
  <Reveal delay={(i % 3) * 0.07} className="break-inside-avoid mb-4">
    <article data-testid={`review-card-${r.review_id}`} className="bento-card rounded-[1.5rem] p-6 md:p-7">
      <div className="flex items-start justify-between gap-3">
        <Stars value={r.rating} />
        <Quote className="w-6 h-6 text-violet-400/50" />
      </div>
      <p className="mt-5 text-slate-200 leading-relaxed">{r.text}</p>
      <div className="mt-6 flex items-center gap-3">
        {r.picture ? (
          <img src={r.picture} alt="" className="w-9 h-9 rounded-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <span className="grid place-items-center w-9 h-9 rounded-full bg-gradient-to-br from-sky-400 to-fuchsia-500 font-semibold text-[#050713]">{r.name?.[0]?.toUpperCase()}</span>
        )}
        <div className="min-w-0">
          <p className="font-semibold text-white truncate">{r.name}</p>
          {r.member ? (
            <span className="inline-flex items-center gap-1 text-xs text-sky-300" data-testid="badge-verified-member"><BadgeCheck className="w-3.5 h-3.5" /> {t.reviews.badgeMember}</span>
          ) : r.verified ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-300" data-testid="badge-verified"><ShieldCheck className="w-3.5 h-3.5" /> {t.reviews.badgeVerified}</span>
          ) : null}
        </div>
      </div>
    </article>
  </Reveal>
);

export const Reviews = () => {
  const { t } = useLang();
  const [data, setData] = useState({ items: [], count: 0, average: 0, verified: 0 });
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/reviews");
      setData({ items: Array.isArray(d?.items) ? d.items : [], count: d?.count ?? 0, average: d?.average ?? 0, verified: d?.verified ?? 0 });
    } catch { /* section stays empty */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => data.items.filter((r) => filter === "all" || (filter === "members" ? r.member : r.verified)), [data.items, filter]);
  const filters = [["all", t.reviews.all], ["members", t.reviews.members], ["verified", t.reviews.verified]];

  return (
    <section id="reviews" className="relative py-28 md:py-40" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Reveal><Overline>{t.reviews.over}</Overline></Reveal>
            <Reveal delay={0.08}><h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] text-white">{t.reviews.title}</h2></Reveal>
            <Reveal delay={0.14}><p className="mt-6 max-w-xl text-slate-300 leading-relaxed">{t.reviews.sub}</p></Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5 flex lg:justify-end gap-8">
            <div data-testid="reviews-average"><p className="font-display text-5xl font-extrabold text-chrome">{data.average || "—"}</p><p className="font-mono text-xs uppercase tracking-widest text-slate-400 mt-1">{t.reviews.average}</p></div>
            <div data-testid="reviews-count"><p className="font-display text-5xl font-extrabold text-white">{data.count}</p><p className="font-mono text-xs uppercase tracking-widest text-slate-400 mt-1">{t.reviews.total}</p></div>
          </Reveal>
        </div>
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {filters.map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`reviews-filter-${k}`}
                className={`rounded-full px-4 py-2 text-sm transition-colors ${filter === k ? "bg-white text-[#050713] font-semibold" : "border border-white/15 text-slate-300 hover:text-white"}`}>
                {label}
              </button>
            ))}
          </div>
          <button onClick={() => setOpen(true)} data-testid="write-review-button" className="btn-chrome rounded-full px-5 py-2.5 text-sm font-semibold text-[#050713] flex items-center gap-2">
            <PenLine className="w-4 h-4" /> {t.reviews.write}
          </button>
        </div>
        <div className="mt-10 columns-1 md:columns-2 lg:columns-3 gap-4">
          {items.map((r, i) => <ReviewCard key={r.review_id} r={r} t={t} i={i} />)}
        </div>
        {items.length === 0 && <p className="mt-6 text-slate-400" data-testid="reviews-empty">{t.reviews.empty}</p>}
      </div>
      <ReviewDialog open={open} onOpenChange={setOpen} onCreated={load} />
    </section>
  );
};
